import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Tag,
  Button,
  Input,
  Select,
  message,
  Card,
  Statistic,
  Row,
  Col,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  TeamOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  ShareAltOutlined,
  MailOutlined,
  UserOutlined,
  IdcardOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { API_CONST } from "../../../const";
import styled from "styled-components";

const { Option } = Select;
const { Text } = Typography;

const Wrapper = styled.div`
  .page-header {
    margin-bottom: 24px;
    h1 {
      font-size: 24px;
      font-weight: 600;
      color: #014c75;
      margin: 0 0 4px 0;
    }
    p {
      font-size: 14px;
      color: #666;
      margin: 0;
    }
  }

  .metrics-row {
    margin-bottom: 24px;
  }

  .metric-card {
    border-radius: 8px;
    .ant-statistic-title {
      font-size: 13px;
      color: #666;
    }
    .ant-statistic-content {
      color: #014c75;
    }
  }

  .filter-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .table-card {
    border-radius: 8px;
  }

  .email-cell {
    font-size: 13px;
    color: #555;
  }

  .id-cell {
    font-family: monospace;
    font-size: 11px;
    color: #888;
    max-width: 160px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const STATUS_COLORS = {
  active: "success",
  pending_invite: "processing",
  declined: "error",
};

const STATUS_LABELS = {
  active: "Active",
  pending_invite: "Pending Invite",
  declined: "Declined",
};

const SOURCE_CONFIG = {
  referral:    { color: "purple",   icon: <GiftOutlined />,      label: "Referral" },
  invite:      { color: "geekblue", icon: <MailOutlined />,      label: "Invite" },
  bench_share: { color: "cyan",     icon: <ShareAltOutlined />,  label: "Bench Share" },
  manual:      { color: "default",  icon: null,                  label: "Manual" },
};

const sourceTag = (src) => {
  const cfg = SOURCE_CONFIG[src] || SOURCE_CONFIG.manual;
  return (
    <Tag color={cfg.color} icon={cfg.icon}>
      {cfg.label}
    </Tag>
  );
};

const fmtDate = (val) =>
  val ? new Date(val).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const inferInvitedBy = (rel) => {
  if (rel.source === "referral" || rel.source === "bench_share") return "Auto-linked";
  if (rel.invitedName || rel.invitedEmail) return "Customer";
  if (rel.status === "active" && !rel.invitedAt) return "Customer";
  return "Customer";
};

const AdminPartnerRelations = () => {
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchRelations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_PARTNERS, {
        headers: { Authorization: `Bearer ${token}` },
        params: { pageSize: 200 },
      });
      setRelations(response.data.relations || []);
    } catch (err) {
      message.error("Failed to load relations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRelations();
  }, [fetchRelations]);

  const filteredData = relations.filter((r) => {
    const q = searchText.toLowerCase();
    const matchesSearch =
      !q ||
      (r.customerOrganization || "").toLowerCase().includes(q) ||
      (r.partnerOrganization  || "").toLowerCase().includes(q) ||
      (r.customerEmail        || "").toLowerCase().includes(q) ||
      (r.partnerEmail         || "").toLowerCase().includes(q) ||
      (r.invitedName          || "").toLowerCase().includes(q) ||
      (r.invitedEmail         || "").toLowerCase().includes(q);
    const matchesStatus = !statusFilter || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalActive    = relations.filter((r) => r.status === "active").length;
  const totalPending   = relations.filter((r) => r.status === "pending_invite").length;
  const totalDeclined  = relations.filter((r) => r.status === "declined").length;
  const uniqueCustomers = new Set(relations.map((r) => r.customerOrganization).filter(Boolean)).size;
  const uniquePartners  = new Set(relations.map((r) => r.partnerOrganization || r.invitedName).filter(Boolean)).size;

  const overviewColumns = [
    {
      title: "Customer Organization",
      dataIndex: "customerOrganization",
      key: "customerOrganization",
      sorter: (a, b) => (a.customerOrganization || "").localeCompare(b.customerOrganization || ""),
      render: (val) => <strong style={{ color: "#014c75" }}>{val || "—"}</strong>,
    },
    {
      title: "Customer Email",
      dataIndex: "customerEmail",
      key: "customerEmail",
      render: (val) =>
        val ? (
          <a href={`mailto:${val}`} className="email-cell">
            {val}
          </a>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>—</Text>
        ),
    },
    {
      title: "Partner Organization",
      key: "partnerOrg",
      sorter: (a, b) =>
        (a.partnerOrganization || a.invitedName || "").localeCompare(
          b.partnerOrganization || b.invitedName || ""
        ),
      render: (_, r) =>
        r.partnerOrganization ? (
          <strong>{r.partnerOrganization}</strong>
        ) : (
          <span>
            {r.invitedName || "—"}{" "}
            <Tag color="default" style={{ fontSize: 11 }}>Offline</Tag>
          </span>
        ),
    },
    {
      title: "Partner Email",
      key: "partnerEmail",
      render: (_, r) => {
        const email = r.partnerEmail || r.invitedEmail;
        return email ? (
          <a href={`mailto:${email}`} className="email-cell">
            {email}
          </a>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>—</Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => (
        <Tag color={STATUS_COLORS[val] || "default"}>
          {STATUS_LABELS[val] || val}
        </Tag>
      ),
    },
    {
      title: "Connected Since",
      dataIndex: "acceptedAt",
      key: "acceptedAt",
      sorter: (a, b) =>
        new Date(a.acceptedAt || 0).getTime() - new Date(b.acceptedAt || 0).getTime(),
      render: fmtDate,
    },
    {
      title: "Invited At",
      dataIndex: "invitedAt",
      key: "invitedAt",
      render: fmtDate,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <span style={{ fontSize: 13, color: "#555" }}>{val}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  const advancedColumns = [
    {
      title: "Relation ID",
      dataIndex: "id",
      key: "id",
      render: (val) => (
        <Tooltip title={val}>
          <span className="id-cell">{val}</span>
        </Tooltip>
      ),
    },
    {
      title: "Customer Org",
      dataIndex: "customerOrganization",
      key: "customerOrganization",
      render: (val) => <strong style={{ color: "#014c75" }}>{val || "—"}</strong>,
    },
    {
      title: "Partner Org",
      key: "partnerOrg",
      render: (_, r) => r.partnerOrganization || r.invitedName || "—",
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (val) => sourceTag(val || "manual"),
      filters: [
        { text: "Manual",     value: "manual" },
        { text: "Invite",     value: "invite" },
        { text: "Referral",   value: "referral" },
        { text: "Bench Share",value: "bench_share" },
      ],
      onFilter: (value, record) => (record.source || "manual") === value,
    },
    {
      title: "Invited By",
      key: "invitedBy",
      render: (_, r) => {
        const by = inferInvitedBy(r);
        const icon =
          by === "Auto-linked" ? <ShareAltOutlined /> :
          by === "Customer"    ? <BankOutlined />    :
                                 <UserOutlined />;
        return (
          <Tag icon={icon} color={by === "Auto-linked" ? "cyan" : "blue"}>
            {by}
          </Tag>
        );
      },
    },
    {
      title: "Invited Name",
      dataIndex: "invitedName",
      key: "invitedName",
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: "Invited Email",
      dataIndex: "invitedEmail",
      key: "invitedEmail",
      render: (val) =>
        val ? (
          <a href={`mailto:${val}`} className="email-cell">{val}</a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Invited Contact",
      dataIndex: "invitedContact",
      key: "invitedContact",
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: (a, b) =>
        new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime(),
      render: fmtDate,
    },
  ];

  const tablePagination = {
    pageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    showTotal: (total) => `Total ${total} relations`,
  };

  const tabItems = [
    {
      key: "overview",
      label: (
        <span>
          <BankOutlined style={{ marginRight: 6 }} />
          Relations Overview
        </span>
      ),
      children: (
        <Table
          columns={overviewColumns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={tablePagination}
          scroll={{ x: 1000 }}
          locale={{ emptyText: "No relations found" }}
        />
      ),
    },
    {
      key: "advanced",
      label: (
        <span>
          <IdcardOutlined style={{ marginRight: 6 }} />
          Advanced Details
        </span>
      ),
      children: (
        <Table
          columns={advancedColumns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={tablePagination}
          scroll={{ x: 1100 }}
          locale={{ emptyText: "No relations found" }}
        />
      ),
    },
  ];

  return (
    <Wrapper>
      <div className="page-header">
        <h1>Partner Relations</h1>
        <p>Monitor all customer-partner relationships across the platform</p>
      </div>

      <Row gutter={16} className="metrics-row">
        <Col span={4}>
          <Card className="metric-card">
            <Statistic title="Total Relations" value={relations.length} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="metric-card">
            <Statistic
              title="Active"
              value={totalActive}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="metric-card">
            <Statistic
              title="Pending Invites"
              value={totalPending}
              valueStyle={{ color: "#1890ff" }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="metric-card">
            <Statistic
              title="Declined"
              value={totalDeclined}
              valueStyle={{ color: "#ff4d4f" }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="metric-card">
            <Statistic title="Unique Customers" value={uniqueCustomers} prefix={<BankOutlined />} />
          </Card>
        </Col>
        <Col span={4}>
          <Card className="metric-card">
            <Statistic title="Unique Partners" value={uniquePartners} prefix={<TeamOutlined />} />
          </Card>
        </Col>
      </Row>

      <div className="filter-bar">
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search by org name, email…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
          allowClear
        />
        <Select
          placeholder={
            <span>
              <FilterOutlined style={{ marginRight: 6 }} />
              Filter by Status
            </span>
          }
          value={statusFilter || undefined}
          onChange={setStatusFilter}
          style={{ width: 200 }}
          allowClear
        >
          <Option value="active">Active</Option>
          <Option value="pending_invite">Pending Invite</Option>
          <Option value="declined">Declined</Option>
        </Select>
        <Button icon={<ReloadOutlined />} onClick={fetchRelations}>
          Refresh
        </Button>
      </div>

      <Card className="table-card">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarStyle={{ marginBottom: 16 }}
        />
      </Card>
    </Wrapper>
  );
};

export default AdminPartnerRelations;
